/**
 * LoginScene.js - Simple Username Login for Memex Racing
 * 
 * A clean and simple login screen that saves username to browser localStorage
 */

import Phaser from 'phaser';

export class LoginScene extends Phaser.Scene {
    constructor() {
        super({ key: 'LoginScene' });
        this.username = '';
        this.inputField = null;
    }

    create() {
        console.log('[LoginScene] Creating login scene');
        console.log('[LoginScene] Available textures:', Object.keys(this.textures.list).filter(key => key.includes('bg')));
        
        // Check if player came from dashboard
        const dashboardPlayer = JSON.parse(sessionStorage.getItem('currentPlayer') || 'null');
        if (dashboardPlayer && dashboardPlayer.playerName) {
            console.log('[LoginScene] Dashboard player detected:', dashboardPlayer.playerName);
            // Skip login, use dashboard player name
            this.username = dashboardPlayer.playerName;
            localStorage.setItem('memexRacingUsername', this.username);
            
            // Go to main menu (not directly to lobby)
            this.scene.start('MainMenuScene', { username: this.username });
            return;
        }
        
        // Get music manager and start playing music
        const musicManager = this.game.registry.get('musicManager');
        if (musicManager) {
            console.log('[LoginScene] Starting background music');
            musicManager.startMusic();
        }
        
        // Get actual game dimensions
        const gameWidth = this.cameras.main.width;
        const gameHeight = this.cameras.main.height;
        const centerX = gameWidth / 2;
        const centerY = gameHeight / 2;
        
        console.log(`[LoginScene] Game dimensions: ${gameWidth}x${gameHeight}`);
        console.log(`[LoginScene] Center point: (${centerX}, ${centerY})`);
        
        // Add the actual background image first
        if (this.textures.exists('login_bg')) {
            console.log('[LoginScene] ✅ Using login_bg texture');
            const mainBg = this.add.image(centerX, centerY, 'login_bg');
            // Scale to cover the entire screen while maintaining aspect ratio
            const scaleX = gameWidth / mainBg.width;
            const scaleY = gameHeight / mainBg.height;
            const scale = Math.max(scaleX, scaleY);
            mainBg.setScale(scale);
            mainBg.setOrigin(0.5, 0.5); // Ensure it's centered
            mainBg.setDepth(-20);
            
            // Debug logging for positioning verification
            console.log('[LoginScene] 📐 Background positioning debug:');
            console.log(`  Position: (${mainBg.x}, ${mainBg.y})`);
            console.log(`  Origin: (${mainBg.originX}, ${mainBg.originY})`);
            console.log(`  Display size: ${mainBg.displayWidth}x${mainBg.displayHeight}`);
            console.log(`  Canvas size: ${this.game.config.width}x${this.game.config.height}`);
            console.log(`  Texture size: ${mainBg.texture.source[0].width}x${mainBg.texture.source[0].height}`);
        } else if (this.textures.exists('main_menu_bg')) {
            console.log('[LoginScene] ✅ Using main_menu_bg texture (fallback)');
            const mainBg = this.add.image(centerX, centerY, 'main_menu_bg');
            // Scale to cover the entire screen while maintaining aspect ratio
            const scaleX = gameWidth / mainBg.width;
            const scaleY = gameHeight / mainBg.height;
            const scale = Math.max(scaleX, scaleY);
            mainBg.setScale(scale);
            mainBg.setOrigin(0.5, 0.5); // Ensure it's centered
            mainBg.setDepth(-20);
            
            // Debug logging for positioning verification
            console.log('[LoginScene] 📐 Background positioning debug:');
            console.log(`  Position: (${mainBg.x}, ${mainBg.y})`);
            console.log(`  Origin: (${mainBg.originX}, ${mainBg.originY})`);
            console.log(`  Display size: ${mainBg.displayWidth}x${mainBg.displayHeight}`);
            console.log(`  Canvas size: ${this.game.config.width}x${this.game.config.height}`);
            console.log(`  Texture size: ${mainBg.texture.source[0].width}x${mainBg.texture.source[0].height}`);
        } else {
            console.warn('[LoginScene] ⚠️ No background textures found!');
        }
        
        // Add very subtle overlay for better text visibility
        const overlay = this.add.graphics();
        overlay.fillStyle(0x000000, 0.3);
        overlay.fillRect(0, 0, gameWidth, gameHeight);
        overlay.setDepth(-19);
        
        // Only create vibrant background if no background image loaded
        if (!this.textures.exists('login_bg') && !this.textures.exists('main_menu_bg')) {
            this.createVibrantMemeBackground();
        }
        
        // Skip meme collage overlay when background image is present
        
        // Main title
        const title = this.add.text(centerX, gameHeight * 0.185, 'M1 PUG RACING', {
            fontSize: '96px',
            fontFamily: 'Courier New, Consolas, monospace',
            color: '#00ff00',
            fontWeight: 'bold',
            stroke: '#000000',
            strokeThickness: 8
        }).setOrigin(0.5);
        
        // Animate title
        this.tweens.add({
            targets: title,
            scaleX: 1.05,
            scaleY: 1.05,
            duration: 2000,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
        });
        
        // Login prompt
        this.add.text(centerX, gameHeight * 0.37, 'Enter Your Username', {
            fontSize: '32px',
            fontFamily: 'Courier New',
            color: '#ffffff',
            fontWeight: 'bold'
        }).setOrigin(0.5);
        
        // Create input field visual
        const inputBg = this.add.graphics();
        const inputWidth = gameWidth * 0.26;
        const inputHeight = gameHeight * 0.056;
        const inputX = centerX - inputWidth / 2;
        const inputY = gameHeight * 0.435;
        inputBg.fillStyle(0x000000, 0.5); // Semi-transparent black
        inputBg.fillRoundedRect(inputX, inputY, inputWidth, inputHeight, 10);
        inputBg.lineStyle(2, 0x00ff00);
        inputBg.strokeRoundedRect(inputX, inputY, inputWidth, inputHeight, 10);
        
        // Username display text
        this.usernameText = this.add.text(centerX, gameHeight * 0.463, '', {
            fontSize: '28px',
            fontFamily: 'Courier New',
            color: '#ffffff',
            fontWeight: 'bold'
        }).setOrigin(0.5);
        
        // Cursor for input field
        this.cursor = this.add.text(centerX, gameHeight * 0.463, '|', {
            fontSize: '28px',
            fontFamily: 'Courier New',
            color: '#00ff00',
            fontWeight: 'bold'
        }).setOrigin(0.5);
        
        // Blinking cursor animation
        this.tweens.add({
            targets: this.cursor,
            alpha: 0,
            duration: 500,
            yoyo: true,
            repeat: -1
        });
        
        // Play button (initially disabled)
        this.playButton = this.createButton(centerX, gameHeight * 0.556, 'PLAY', () => {
            this.startGame();
        });
        this.updatePlayButton();
        
        // Instructions
        this.add.text(centerX, gameHeight * 0.648, 'Type your username and press ENTER or click PLAY', {
            fontSize: '22px',
            fontFamily: 'Courier New',
            color: '#ffffff',
            stroke: '#000000',
            strokeThickness: 3
        }).setOrigin(0.5);
        
        // Check for existing username in localStorage
        const savedUsername = localStorage.getItem('memexRacingUsername');
        if (savedUsername) {
            this.username = savedUsername;
            this.usernameText.setText(this.username);
            this.updatePlayButton();
            
            // Show welcome back message
            const welcomeText = this.add.text(centerX, gameHeight * 0.324, `Welcome back, ${savedUsername}!`, {
                fontSize: '24px',
                fontFamily: 'Courier New',
                color: '#00ff00'
            }).setOrigin(0.5);
            
            // Fade out welcome message
            this.tweens.add({
                targets: welcomeText,
                alpha: 0,
                delay: 2000,
                duration: 1000
            });
        }
        
        // Setup keyboard input
        this.setupKeyboardInput();
        
        // Fade in
        this.cameras.main.fadeFrom(500, 0, 0, 0);
    }
    
    /**
     * Create vibrant meme-style background
     */
    createVibrantMemeBackground() {
        // Set base gradient background
        const graphics = this.add.graphics();
        
        // Create rainbow gradient effect - matching MainMenuScene
        const colors = [
            { color: 0xff00ff, stop: 0 },    // Magenta
            { color: 0x00ffff, stop: 0.2 },  // Cyan
            { color: 0xffff00, stop: 0.4 },  // Yellow
            { color: 0x00ff00, stop: 0.6 },  // Green
            { color: 0xff0088, stop: 0.8 },  // Pink
            { color: 0x8800ff, stop: 1 }     // Purple
        ];
        
        // Draw diagonal gradient stripes
        for (let i = 0; i < 20; i++) {
            const colorIndex = i % colors.length;
            graphics.fillStyle(colors[colorIndex].color, 0.12);
            graphics.fillRect(i * 100 - 200, 0, 300, 1080);
            
            // Animate the stripes
            const stripe = this.add.graphics();
            stripe.fillStyle(colors[colorIndex].color, 0.03);
            stripe.fillRect(0, 0, 100, 1080);
            stripe.x = i * 100;
            stripe.setDepth(-10);
            
            this.tweens.add({
                targets: stripe,
                x: stripe.x + 100,
                duration: 5000 + (i * 200),
                repeat: -1,
                ease: 'Linear'
            });
        }
        
        // Add floating meme text elements
        const memeTexts = ['WAGMI', 'TO THE MOON', 'HODL', 'PUG LIFE', 'M TOKEN', 'RACE'];
        for (let i = 0; i < 10; i++) {
            const x = Math.random() * 1920;
            const y = Math.random() * 1080;
            const text = this.add.text(x, y, memeTexts[Math.floor(Math.random() * memeTexts.length)], {
                fontSize: '36px',
                fontFamily: 'Impact, Arial Black',
                color: '#' + Math.floor(Math.random()*16777215).toString(16),
                fontWeight: 'bold',
                stroke: '#000000',
                strokeThickness: 3
            }).setAlpha(0.15).setDepth(-9);
            
            this.tweens.add({
                targets: text,
                x: x + Phaser.Math.Between(-150, 150),
                y: y + Phaser.Math.Between(-150, 150),
                rotation: Phaser.Math.Between(-0.5, 0.5),
                alpha: { from: 0.1, to: 0.25 },
                duration: Phaser.Math.Between(8000, 12000),
                yoyo: true,
                repeat: -1,
                ease: 'Sine.easeInOut'
            });
        }
        
        // Add pulsing neon grid
        const gridGraphics = this.add.graphics();
        gridGraphics.lineStyle(1, 0x00ff00, 0.08);
        for (let x = 0; x < 1920; x += 60) {
            gridGraphics.moveTo(x, 0);
            gridGraphics.lineTo(x, 1080);
        }
        for (let y = 0; y < 1080; y += 60) {
            gridGraphics.moveTo(0, y);
            gridGraphics.lineTo(1920, y);
        }
        gridGraphics.strokePath();
        gridGraphics.setDepth(-8);
        
        // Pulse the grid
        this.tweens.add({
            targets: gridGraphics,
            alpha: { from: 0.05, to: 0.12 },
            duration: 2500,
            yoyo: true,
            repeat: -1
        });
    }
    
    /**
     * Create animated background (fallback)
     */
    createAnimatedBackground() {
        // Racing lines
        for (let i = 0; i < 10; i++) {
            const line = this.add.graphics();
            line.lineStyle(2, 0x003300, 0.3);
            line.beginPath();
            line.moveTo(0, 100 + i * 80);
            line.lineTo(1920, 100 + i * 80);
            line.strokePath();
            
            this.tweens.add({
                targets: line,
                x: -100,
                duration: 3000 + i * 200,
                repeat: -1,
                ease: 'Linear'
            });
        }
        
        // Floating elements
        for (let i = 0; i < 6; i++) {
            const element = this.add.circle(
                Phaser.Math.Between(-100, 1920),
                Phaser.Math.Between(200, 800),
                Phaser.Math.Between(3, 6),
                0x00ff00,
                0.2
            );
            
            this.tweens.add({
                targets: element,
                x: 2000,
                duration: Phaser.Math.Between(10000, 20000),
                repeat: -1,
                delay: i * 1500
            });
        }
    }
    
    /**
     * Setup keyboard input handling
     */
    setupKeyboardInput() {
        // Listen for any key press
        this.input.keyboard.on('keydown', (event) => {
            const key = event.key;
            
            // Handle special keys
            if (key === 'Enter') {
                if (this.username.length >= 3) {
                    this.startGame();
                }
                return;
            }
            
            if (key === 'Backspace') {
                this.username = this.username.slice(0, -1);
                this.updateUsernameDisplay();
                return;
            }
            
            // Handle regular characters (letters, numbers, underscore)
            if (key.length === 1 && /[a-zA-Z0-9_]/.test(key)) {
                if (this.username.length < 20) { // Max 20 characters
                    this.username += key;
                    this.updateUsernameDisplay();
                }
            }
        });
    }
    
    /**
     * Update username display
     */
    updateUsernameDisplay() {
        this.usernameText.setText(this.username);
        
        // Update cursor position
        const textWidth = this.usernameText.width;
        this.cursor.x = 960 + textWidth / 2 + 5;
        
        // Update play button state
        this.updatePlayButton();
    }
    
    /**
     * Update play button state
     */
    updatePlayButton() {
        const isValid = this.username.length >= 3;
        
        if (isValid) {
            this.playButton.button.setFillStyle(0x00ff00);
            this.playButton.text.setColor('#ffffff');
            this.playButton.button.setInteractive({ useHandCursor: true });
        } else {
            this.playButton.button.setFillStyle(0x666666);
            this.playButton.text.setColor('#666666');
            this.playButton.button.removeInteractive();
        }
    }
    
    /**
     * Create a button
     */
    createButton(x, y, text, callback) {
        // Button background - transparent with green outline
        const button = this.add.rectangle(x, y, 200, 60, 0x000000, 0.3);
        button.setStrokeStyle(3, 0x00ff00);
        
        // Button text
        const buttonText = this.add.text(x, y, text, {
            fontSize: '28px',
            fontFamily: 'Courier New',
            color: '#ffffff',
            fontWeight: 'bold'
        }).setOrigin(0.5);
        
        // Hover effects
        button.on('pointerover', () => {
            if (button.input.enabled) {
                button.setScale(1.1);
                buttonText.setScale(1.1);
            }
        });
        
        button.on('pointerout', () => {
            button.setScale(1);
            buttonText.setScale(1);
        });
        
        // Click handler
        button.on('pointerdown', () => {
            if (button.input.enabled) {
                callback();
            }
        });
        
        return { button, text: buttonText };
    }
    
    /**
     * Start the game with the entered username
     */
    startGame() {
        if (this.username.length < 3) {
            console.log('[LoginScene] Username too short');
            return;
        }
        
        console.log('[LoginScene] Starting game with username:', this.username);
        
        // Save username to localStorage
        localStorage.setItem('memexRacingUsername', this.username);
        
        // Store username globally for use in other scenes
        this.game.playerUsername = this.username;
        
        // Start music now that we have user interaction
        const musicManager = this.game.registry.get('musicManager');
        if (musicManager && !musicManager.isPlaying) {
            console.log('[LoginScene] Starting music after user interaction');
            musicManager.startMusic();
        }
        
        // Fade out and transition to main menu
        this.cameras.main.fadeOut(500, 0, 0, 0);
        
        this.cameras.main.once('camerafadeoutcomplete', () => {
            this.scene.start('MainMenuScene', { username: this.username });
        });
    }
}